// file: src/hooks/useOpenCitation.js
import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { showError } from '../utils/errorUtils';
import { normalizeChunk, getCleanFileName, chunkByIndex, refNavigation} from '../utils/citationService';
import { detectFileType } from '../utils/fileUtils';
import { useProjectContext } from '../context/ProjectContext';
import { useChatContext } from '../context/ChatContext';
import { useViewerContext } from '../context/ViewerContext';
import FileTypeIcon from '../components/icons/FileTypeIcon';
import styles from './citation.module.css';

// CitationLink Component
export function CitationLink({ index, reference, chunk, children }) {
  const openCitation = useOpenCitation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const linkRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const isClickingRef = useRef(false);
  const clickProtectionTimeoutRef = useRef(null);
  
  // Get the chunk data
  const citationChunk = chunk || (index != null && reference ? chunkByIndex(reference, index) : null);
  const normalizedChunk = citationChunk ? normalizeChunk(citationChunk) : null;
  
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set clicking flag to prevent tooltip from hiding
    isClickingRef.current = true;
    
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    // Keep protection active for a short time after click
    if (clickProtectionTimeoutRef.current) {
      clearTimeout(clickProtectionTimeoutRef.current);
    }
    clickProtectionTimeoutRef.current = setTimeout(() => {
      isClickingRef.current = false;
    }, 500); // 500ms protection after click
    
    if (!citationChunk) {
      // Check if this is due to empty chunks in the reference
      if (reference && Array.isArray(reference.chunks) && reference.chunks.length === 0) {
        console.warn('[CitationLink] Reference exists but has no chunks - data may still be loading');
      } else {
        console.error('[CitationLink] Missing reference data for citation');
      }
      return;
    }
    
    openCitation(citationChunk);
  };
  
  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    // Show tooltip with a slight delay to prevent flashing on quick hovers
    if (normalizedChunk && !normalizedChunk.isSynthesized && normalizedChunk.documentName) {
      // Clear any pending show timeout before setting a new one
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      
      showTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
        showTimeoutRef.current = null; // Clear ref after execution
        
        // Calculate position
        if (linkRef.current) {
          const rect = linkRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Tooltip dimensions (approximate)
          const tooltipWidth = 280;
          const tooltipHeight = 60;
          
          // Default position: centered above the link
          let top = rect.top - tooltipHeight - 8; // 8px gap above link
          let left = rect.left + (rect.width / 2);
          
          // Check if tooltip would go off top of screen
          if (top < 10) {
            // Position below instead
            top = rect.bottom + 8;
          }
          
          // Check horizontal bounds
          if (left - (tooltipWidth / 2) < 10) {
            left = (tooltipWidth / 2) + 10; // 10px from left edge
          } else if (left + (tooltipWidth / 2) > viewportWidth - 10) {
            left = viewportWidth - (tooltipWidth / 2) - 10; // 10px from right edge
          }
          
          setTooltipPosition({ top, left });
        }
      }, 100); // 100ms delay before showing
    }
  };
  
  const handleMouseLeave = () => {
    // Don't hide if we're in the middle of clicking
    if (isClickingRef.current) {
      return;
    }
    
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    
    // Clear any existing hide timeout before setting a new one
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    
    // Hide tooltip with a delay to allow moving to tooltip content
    hideTimeoutRef.current = setTimeout(() => {
      // Double-check we're not clicking before hiding
      if (!isClickingRef.current) {
        setShowTooltip(false);
        hideTimeoutRef.current = null; // Clear ref after execution
      }
    }, 300); // 300ms delay before hiding
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (clickProtectionTimeoutRef.current) clearTimeout(clickProtectionTimeoutRef.current);
    };
  }, []);
  
  // Update tooltip position if window is resized while tooltip is visible
  useEffect(() => {
    // Only add listeners when tooltip is actually shown
    if (!showTooltip) return;
    
    const updatePosition = () => {
      if (linkRef.current && showTooltip) {
        const rect = linkRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        const tooltipWidth = 280;
        const tooltipHeight = 60;
        
        let top = rect.top - tooltipHeight - 8;
        let left = rect.left + (rect.width / 2);
        
        if (top < 10) {
          top = rect.bottom + 8;
        }
        
        if (left - (tooltipWidth / 2) < 10) {
          left = (tooltipWidth / 2) + 10;
        } else if (left + (tooltipWidth / 2) > viewportWidth - 10) {
          left = viewportWidth - (tooltipWidth / 2) - 10;
        }
        
        setTooltipPosition({ top, left });
      }
    };
    
    // Use passive listeners for better performance
    window.addEventListener('resize', updatePosition, { passive: true });
    window.addEventListener('scroll', updatePosition, { passive: true });
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [showTooltip]);
  
  const displayName = normalizedChunk && normalizedChunk.documentName 
    ? getCleanFileName(normalizedChunk.documentName) 
    : 'Unknown Document';
    
  const pageNumber = normalizedChunk?.page || 1;
  const fileType = normalizedChunk?.documentName ? detectFileType(normalizedChunk.documentName) : 'unknown';
  
  // Render tooltip content
  const tooltipContent = showTooltip && normalizedChunk && !normalizedChunk.isSynthesized && (
    <div
      className={styles.citationTooltip}
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`
      }}
    >
      <div className={styles.tooltipContent}>
        <FileTypeIcon type={fileType} className={styles.tooltipIcon} />
        <div className={styles.tooltipText}>
          <div className={styles.tooltipTitle} title={displayName}>
            {displayName}
          </div>
          <div className={styles.tooltipPage}>
            Page {pageNumber}
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <>
      <sup 
        ref={linkRef}
        className={normalizedChunk?.isSynthesized ? styles.synthesizedCitation : styles.citationLink}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={normalizedChunk?.isSynthesized ? normalizedChunk.snippet : ""}  // Show snippet as tooltip for synthesized
      >
        {children ?? `[${index != null ? index + 1 : '?'}]`}
      </sup>
      
      {/* Use React Portal to render tooltip at body level */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
}

// useOpenCitation Hook
export default function useOpenCitation() {
  const { projects, activeProjectId } = useProjectContext();
  const { findOrCreateDialogForProject } = useChatContext();
  const { openFileAtPage, openFiles } = useViewerContext();

  // OPTIMIZATION: Create a document index for O(1) lookups by ID
  const documentIndex = useMemo(() => {
    const index = new Map();
    
    // Index all documents from all projects
    for (const project of projects || []) {
      if (!project.phases) continue;
      for (const phase of project.phases) {
        if (!phase.files) continue;
        for (const file of phase.files) {
          // Store with both string and original ID for flexibility
          index.set(file.id, {
            ...file,
            kb_id: phase.kb_id,
            project_id: project.id
          });
          index.set(String(file.id), {
            ...file,
            kb_id: phase.kb_id,
            project_id: project.id
          });
        }
      }
    }
    
    return index;
  }, [projects]);

  return useCallback(async (rawChunk) => {
    const chunk = normalizeChunk(rawChunk);
    if (!chunk) {
      showError('Invalid citation data');
      return;
    }

    // Check if this citation can be navigated to
    if (!chunk.canNavigate) {
      // Provide a more helpful message based on the type of synthesized content
      let errorMessage = 'This citation cannot be navigated to a specific document location';
      
      if (chunk.isSynthesized) {
        if (chunk.synthesisType === 'knowledge_graph') {
          errorMessage = 'This citation is from synthesized knowledge graph information and cannot be navigated to a specific document';
        } else {
          errorMessage = 'This citation is from aggregated information and cannot be navigated to a specific document';
        }
      } else if (!chunk.id) {
        errorMessage = 'This citation is missing document identification information';
      } else if (!chunk.page) {
        errorMessage = 'This citation is missing page number information';
      }
      
      showError(errorMessage);
      return;
    }

    // 2. Try to locate the document - OPTIMIZED SEARCH
    let file = null;
    
    // OPTIMIZATION 1: Use document index for instant ID lookup
    if (chunk.id) {
      // Check if file is already open (fastest path)
      file = openFiles.find(f => f.id === chunk.id || String(f.id) === String(chunk.id));
      
      // If not open, use the index for O(1) lookup
      if (!file) {
        file = documentIndex.get(chunk.id) || documentIndex.get(String(chunk.id));
      }
    }
    
    // OPTIMIZATION 2: If not found by ID, check by name (only if no ID)
    if (!file && chunk.documentName && chunk.documentName !== 'Unknown Document') {
      const cleanName = getCleanFileName(chunk.documentName);
      
      // Check open files first
      file = openFiles.find(f => {
        const fName = getCleanFileName(f.name);
        return fName === cleanName || fName.includes(cleanName) || cleanName.includes(fName);
      });
      
      // If not in open files, search the document index
      if (!file) {
        for (const [, doc] of documentIndex) {
          const fName = getCleanFileName(doc.name);
          if (fName === cleanName || fName.includes(cleanName) || cleanName.includes(fName)) {
            file = doc;
            break;
          }
        }
      }
    }
    
    if (!file) {
      // Show more helpful error with available info
      const errorMessage = chunk.documentName && chunk.documentName !== 'Unknown Document' 
        ? `Document not found: "${getCleanFileName(chunk.documentName)}" is not available`
        : 'Document not found: The cited document is not available';
      showError(errorMessage, 'Document Not Found', { autoHideDelay: 6000 });
      return;
    }

    // 3. Only switch projects if absolutely necessary
    if (file.project_id && file.project_id !== activeProjectId) {
      try {
        // This is the expensive operation - only do it when necessary
        await findOrCreateDialogForProject(file.project_id);
      } catch (err) {
        showError('Failed to switch to document\'s project');
        return;
      }
    }

    // 4. Navigate to the file and page immediately
    openFileAtPage({ 
      file, 
      page: chunk.page
    });
    
  }, [projects, activeProjectId, openFileAtPage, findOrCreateDialogForProject, openFiles, documentIndex]);
}

// Custom hook for doc navigation
export function useDocNavigation({ containerRef, pageClass, onPageChange }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const scrollHandlerRef = useRef(null);
  const isButtonScrollRef = useRef(false);

  // Calculate current page based on scroll position
  const updateCurrentPage = useCallback(() => {
    if (!containerRef.current || isButtonScrollRef.current) {
      isButtonScrollRef.current = false;
      return;
    }

    const container = containerRef.current;
    const pages = container.getElementsByClassName(pageClass);
    const newPage = refNavigation.calculateCurrentPage(container, pages);
    
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      onPageChange?.(newPage);
    }
  }, [currentPage, pageClass, onPageChange]);

  // Navigate to specific page
  const goToPage = useCallback((targetPage, instant = false) => {
    if (!containerRef.current || !numPages || targetPage < 1 || targetPage > numPages) {
      return;
    }
    
    const container = containerRef.current;
    const pages = container.getElementsByClassName(pageClass);
    
    if (!pages.length || pages.length < targetPage) { 
      console.error(`[goToPage] Page element ${targetPage} not found`);
      return; 
    }

    isButtonScrollRef.current = true;
    setCurrentPage(targetPage);
    
    const targetEl = pages[targetPage - 1];
    refNavigation.scrollToPage(container, targetEl, instant);
  }, [numPages, pageClass]);

  // Set up scroll handler
  useEffect(() => {
    if (!containerRef.current) return;

    // Create throttled scroll handler
    scrollHandlerRef.current = refNavigation.createThrottledScrollHandler(updateCurrentPage, 50);
    
    const container = containerRef.current;
    container.addEventListener('scroll', scrollHandlerRef.current, { passive: true });

    return () => {
      if (scrollHandlerRef.current) {
        container.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [updateCurrentPage]);

  return {
    currentPage,
    setCurrentPage,
    numPages,
    setNumPages,
    goToPage,
    formatPageInfo: (current = currentPage, total = numPages) => 
      refNavigation.formatPageInfo(current, total)
  };
}